import React, {useEffect, useMemo, useState} from 'react';
import clsx from 'clsx';
import {SelectField} from '@netx/core-components/SelectField';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {enumToValues} from '@netx/core-next/utils/enum';
import {UserAutoList} from '@netx/core-user-next/components/UserAutoList';
import {SearchRuleWarningLabel, SearchSubTypeLabel} from '../../../../locales';
import type {CommonRuleEditorProps} from '../../../types';
import {CheckoutType} from '../../constants';
import {getCheckoutsTypeLabel} from '../../utils';
import {
	CHECKOUTS_RULE_EDITOR_CLASS_NAME,
	checkoutsRuleEditorClasses,
	CheckoutsRuleEditorClasses,
	getCheckoutsRuleEditorUtilityClass,
} from './checkoutsRuleEditorClasses';

export type CheckoutsRuleEditorOwnerState = WithClasses<
	CheckoutsRuleEditorClasses,
	{}
>;

const useUtilityClasses = (ownerState: CheckoutsRuleEditorOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getCheckoutsRuleEditorUtilityClass, classes);
};

const CheckoutsRuleEditorRoot = styled('div', {
	name: CHECKOUTS_RULE_EDITOR_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	'& > * + *': {
		marginLeft: theme.spacing(1),
	},
}));

export type CheckoutsRuleEditorProps = WithClasses<
	CheckoutsRuleEditorClasses,
	Pick<CommonRuleEditorProps, 'validationErrors'> & {
		elementSubType1: CheckoutType;
		elementSubType1Options?: CheckoutType[];
		elementValue1: number;
		elementValue1Unavailable: boolean;
		elementValue1Label: string;
		elementValue1Loading: boolean;
		onSubType1Change: (subType1: CheckoutType) => void;
		onValue1Change: (value: number) => void;
	}
>;

const CheckoutsRuleEditorInner = ({
	classes: _classes,
	className,
	elementSubType1,
	elementSubType1Options,
	elementValue1,
	elementValue1Unavailable,
	elementValue1Label,
	elementValue1Loading,
	validationErrors,
	onSubType1Change,
	onValue1Change,
}: CheckoutsRuleEditorProps) => {
	const ownerState: CheckoutsRuleEditorOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	const [userName, setUserName] = useState('');

	useEffect(() => {
		setUserName(elementValue1Label);
	}, [elementValue1Label]);

	const waitingForUserLabel = elementValue1Loading && userName === '';

	const _elementSubType1Options = useMemo(
		() => elementSubType1Options || enumToValues(CheckoutType),
		[elementSubType1Options],
	);

	return (
		<CheckoutsRuleEditorRoot className={clsx(classes.root, className)}>
			<SelectField<CheckoutType, false, true, false>
				data-testid="checkouts-rule-sub-type-1"
				disableClearable
				options={_elementSubType1Options}
				label={SearchSubTypeLabel.MATCH_TYPE}
				name={'rule-sub-type-1'}
				value={elementSubType1}
				getOptionLabel={getCheckoutsTypeLabel}
				onChange={(event, value) => onSubType1Change(value)}
			/>

			{elementSubType1 === CheckoutType.USER_ID ? (
				<UserAutoList<false, true>
					data-testid="checkouts-rule-value-1"
					disableClearable
					error={validationErrors?.elementValue1?.message}
					loadingValue={elementValue1Loading}
					name="rule-value-1"
					placeholder={
						waitingForUserLabel
							? 'loading...'
							: elementValue1Unavailable
							? SearchRuleWarningLabel.DATA_UNAVAILABLE
							: undefined
					}
					value={
						elementValue1Unavailable || waitingForUserLabel
							? (null as any)
							: {
									name: userName,
									id: elementValue1,
							  }
					}
					onChange={(event, user) => {
						if (!user) return;
						setUserName(user.name);
						onValue1Change(user.id);
					}}
				/>
			) : null}
		</CheckoutsRuleEditorRoot>
	);
};

export const CheckoutsRuleEditorPrimitive = CheckoutsRuleEditorInner;

export const CheckoutsRuleEditor = CheckoutsRuleEditorPrimitive;

export default CheckoutsRuleEditor;

export type CheckoutsRuleEditorGenericProps = WithClasses<
	CheckoutsRuleEditorClasses,
	CommonRuleEditorProps & {}
>;

export const CheckoutsRuleEditorGenericPrimitive = ({
	elementValue1,
	...props
}: CheckoutsRuleEditorGenericProps) => {
	return (
		<CheckoutsRuleEditor
			{...props}
			elementValue1={parseInt(elementValue1.toString()) || 0}
		/>
	);
};

export const CheckoutsRuleEditorGeneric = CheckoutsRuleEditorGenericPrimitive;
